import { observer } from "mobx-react-lite";
import SmartApp from "./SmartApp";
import { useNavigate } from "react-router-dom";
import useUrlQueryParams from "e.sic-platform.webclient/src/services/url/UrlQueryService";
import { useEffect, useState } from "react";
import { Grid, Stack, Typography } from "@mui/material";
import LoadingSpinner from "e.sic-platform.webclient/src/components/loading-spinner/LoadingSpinner";
import SmartAppStep from "./SmartAppStep";
import DropFiles from "e.sic-platform.webclient/src/components/upload-drop-files/DropFiles";
import { uploadFilesService } from "e.sic-platform.webclient/src/services/upload/UploadFilesService";
import UploadTable from "e.sic-platform.webclient/src/components/upload-drop-files/UploadTable";
import PurchaseConfirmation from "./PurchaseConfirmation";
import UploadStatus from "e.sic-platform.webclient/src/components/upload-status/UploadStatus";
import CopyUrlButton from "../buttons/copy-url-button/CopyUrlButton";
import { AppStorage } from "e.sic-platform.webclient/src/services/app/AppStorage";
import { ActivityTypes } from "e.sic-platform.webclient/src/services/shop/ActivityPricesStorage";
import RunAppButton from "../buttons/run-app-button/RunAppButton";
import { Icon } from "e.sic-platform.webclient/src/icons/Icon";
import { translationService } from "e.sic-platform.webclient/src/services/translation/TranslationService";
import { PubSubEvents } from "e.sic-platform.webclient/src/services/PubSubEvents";
import { endpoints } from "e.sic-platform.webclient/src/services/api/ApiEndpoints";
import { IServiceStorageEnv } from "e.sic-platform.webclient/src/services/app/ServiceStorage";
import { SmartAppContext } from "./SmartAppContext";

export interface ISmartAppBaseProps {
    appStorage: AppStorage,
    icon: Icon | undefined,
    actionBar?: any;
    stickyActionBar?: boolean;
    fileTypesAccepted?: string;
    // Translation key for the drop zone's first line (default: the generic "file" wording).
    dropInstructionKey?: string;
    // Content shown ABOVE the drop zone in the upload step (Inspection Insights: the service
    // choice with its example frame), for apps where choosing comes before the file.
    step1Top?: any;
    onDroppedFiles?: (files: any) => void;
    hideCopyUrl?: boolean;
    freeActivity?: boolean;
    hideToolbar?: boolean;
    hideImage?: boolean;
    smallTitle?: boolean;
    step1Extras?: any;
    hideUploadUI?: boolean;
    hideUploadStepHeader?: boolean;
    // Panel shown next to the app description in the header (see ISmartApp.headerAside).
    headerAside?: any;
    // Custom header description in place of the app's paragraph (see ISmartApp.headerDescription).
    headerDescription?: any;
    // Override the upload-step purchase dialog (Inspection Insights: the user picks
    // which services to run; the dialog shows the combined DB price for that set).
    purchaseApps?: ActivityTypes[];
    onPurchaseConfirm?: () => void;
    hideRedo?: boolean;
    children: any
}

export default observer(function SmartAppBase(props: ISmartAppBaseProps) {
    const stickyActionBar = props.stickyActionBar ?? false;
    return (
        <SmartAppContext.Provider value={{ stickyActionBar }}>
            <SmartApp
                appStorage={props.appStorage}
                actionBar={props.actionBar}
                hideToolbar={props.hideToolbar}
                hideImage={props.hideImage}
                smallTitle={props.smallTitle}
                hideRedo={props.hideRedo}
                headerAside={props.headerAside}
                headerDescription={props.headerDescription}
            >
                <AppPage
                    appStorage={props.appStorage}
                    icon={props.icon}
                    fileTypesAccepted={props.fileTypesAccepted}
                    dropInstructionKey={props.dropInstructionKey}
                    step1Top={props.step1Top}
                    onDroppedFiles={props.onDroppedFiles}
                    hideCopyUrl={props.hideCopyUrl}
                    freeActivity={props.freeActivity}
                    step1Extras={props.step1Extras}
                    hideUploadUI={props.hideUploadUI}
                    hideUploadStepHeader={props.hideUploadStepHeader}
                    purchaseApps={props.purchaseApps}
                    onPurchaseConfirm={props.onPurchaseConfirm}
                >
                    {props.children}
                </AppPage>
            </SmartApp>
        </SmartAppContext.Provider>
    )
})

export const AppPage = observer(function AppPage(props: ISmartAppBaseProps) {
    const {appStorage, icon} = {...props};
    const navigate = useNavigate();
    const { guid }: any = useUrlQueryParams();
    const [confirmationPending, setConfirmationPending] = useState(false);

    useEffect(() => {
        if (appStorage.navigate !== "") {
            navigate(appStorage.navigate)
            appStorage.resetNavigate()
        }
    }, [appStorage.navigate, appStorage.resetNavigate])

    useEffect(() => {
    async function loadIds() {
      try {
            if (guid !== undefined) {
            appStorage.setFileId(await appStorage.getFileId(guid));
            appStorage.setFileGuid(guid)
            appStorage.load();
        }
        if (appStorage.fileId !== undefined) {
            navigate(appStorage.fileRoute);
        }
      } catch (err) {
        appStorage.setError("general.apps.error")
      }
    }
    loadIds()
    }, [guid])

    useEffect(() => {
        uploadFilesService.clear();
    }, [])

    useEffect(() => {
        const subscribeToken = PubSub.subscribe(
            PubSubEvents.VideoConversionFinished,
            (_event: string, notification: Notification) => {
                console.log(notification);
                appStorage.onConversionFinished(notification.data.fileId);
            }
      );
        return () => { PubSub.unsubscribe(subscribeToken); };
    }, [appStorage.onConversionFinished])

    const isConverting = appStorage.fileId !== undefined &&
        appStorage.serviceList.some((s: any) => s?.video !== undefined && !s.video.isConverted);
    useEffect(() => {
        if (!isConverting) return;
        const interval = window.setInterval(() => {
            appStorage.serviceList.forEach((s: any) => {
                if (typeof s?.pollUntilReady === "function") {
                    void s.pollUntilReady();
                }
            });
        }, 10000);
        return () => window.clearInterval(interval);
    }, [isConverting, appStorage]);

    if (appStorage.fileId === undefined) appStorage.setStep(1);
    if (appStorage.fileId && appStorage.step<2) appStorage.setStep(2);

    if (appStorage.errorMessage) return <Grid item xs={12}><Typography color="red">{translationService.translate(appStorage.errorMessage)}</Typography></Grid>
    if (appStorage.isLoading) return <Grid item xs={12}><LoadingSpinner /></Grid>

    return (
        <>
            <SmartAppStep
                titleKey="videoAnalysis.upload.title"
                descriptionKey="videoAnalysis.upload.description"
                visible={appStorage.fileId === undefined}
                step={1}
                appStorage={appStorage}
                hideStepHeader={props.hideUploadStepHeader}
            >
                {props.step1Top && (
                    <Grid item xs={12} sm={12}>
                        {props.step1Top}
                    </Grid>
                )}
                <Grid item xs={12} sm={12}>
                    <DropFiles
                        singleFile
                        onDroppedFiles={props.onDroppedFiles ?? uploadFilesService.addFilesForUpload}
                        fileTypesAccepted={props.fileTypesAccepted}
                        instructionKey={props.dropInstructionKey}
                    />
                </Grid>
                <Grid item xs={12} sm={12} className="centered">
                    {!props.hideUploadUI && appStorage.canRun &&
                        <UploadTable files={uploadFilesService.filesForUpload} onRemoveFile={uploadFilesService.removeFile} />
                    }
                </Grid>
                {props.step1Extras && (
                    <Grid item xs={12} sm={12} className="centered">
                        {props.step1Extras}
                    </Grid>
                )}
                <Grid item xs={12} sm={12} className="centered">
                    <Stack direction="row" alignItems="center">
                        {props.freeActivity ? (
                            <RunAppButton disabled={!appStorage.canRun || uploadFilesService.uploadProgressModalIsVisbile} onClick={() => appStorage.upload()} icon={icon}/>
                        ) : (
                            <>
                                <RunAppButton disabled={!appStorage.canRun} onClick={() => setConfirmationPending(!confirmationPending)} icon={icon}/>
                                <PurchaseConfirmation
                                    apps={props.purchaseApps ?? appStorage.activityType}
                                    visible={confirmationPending}
                                    confirm={props.onPurchaseConfirm ?? appStorage.upload}
                                    toggle={() => setConfirmationPending(!confirmationPending)}
                                />
                            </>
                        )}
                    </Stack>
                </Grid>
                <Grid item xs={12} sm={12}>
                    {!props.hideUploadUI && uploadFilesService.uploadProgressModalIsVisbile && <UploadStatus />}
                </Grid>
            </SmartAppStep>
            {props.children}
            <Grid item xs={12} sm={12}>
                {!props.hideCopyUrl && appStorage.fileId !== undefined && <CopyUrlButton id={appStorage.fileId} />}
            </Grid>
        </>
    )
})
